import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

interface CallRecording {
  callRecordingId?: string | number;
  officerExtension?: string;
  citizenNumber?: string;
  calledNumber?: string;
  direction?: string;
  durationSeconds?: number;
  recordedAt?: string;
  fileName?: string;
  transcriptText?: string;
  transcriptionStatus?: string;
  transcriptionError?: string;
  transcriptionRequestedAt?: string;
  transcriptionCompletedAt?: string;
  transcriptionAttempts?: number;
  wrapUpNotes?: string;
  disposition?: string;
  callReason?: string;
  sourceOfInformation?: string;
  callbackRequired?: boolean;
  callbackDueAt?: string;
  callbackStatus?: string;
}

interface TranscriptLine {
  timeLabel: string;
  startSec: number;
  speaker: string;
  text: string;
  isAgent: boolean;
}

type SortKey = 'recordedAt' | 'officerExtension' | 'citizenNumber' | 'direction' | 'durationSeconds';

const API_URL = environment.apiHost;

@Component({
  selector: 'app-csr-history',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './csr-history.component.html',
  styleUrls: ['./csr-history.component.scss'],
})
export class CsrHistoryComponent implements OnInit, OnDestroy {
  agentExt = localStorage.getItem('grmis-sip-ext') || '';
  recordings: CallRecording[] = [];
  downloadingIds  = new Set<string | number>();
  audioLoadingIds = new Set<string | number>();
  private readonly audioBlobUrls = new Map<string | number, string>();
  loading = false;
  query = '';
  directionFilter = 'all';
  fromDate = '';
  toDate   = '';
  sortBy: SortKey = 'recordedAt';
  sortDir: 'asc' | 'desc' = 'desc';
  transcriptModal: CallRecording | null = null;
  private transcriptionPoll?: ReturnType<typeof setInterval>;

  constructor(private readonly router: Router) {}

  ngOnInit() {
    this.loadRecordings();
  }

  ngOnDestroy() {
    this.stopTranscriptionPolling();
    this.audioBlobUrls.forEach(url => URL.revokeObjectURL(url));
    this.audioBlobUrls.clear();
  }

  async loadAudio(id?: string | number) {
    if (!id || this.audioLoadingIds.has(id) || this.audioBlobUrls.has(id)) return;
    this.audioLoadingIds.add(id);
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(this.audioUrl(id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const blob = await res.blob();
        this.audioBlobUrls.set(id, URL.createObjectURL(blob));
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (error) {
      console.error('Audio load failed', error);
    } finally {
      this.audioLoadingIds.delete(id);
    }
  }

  getAudioBlobUrl(id?: string | number): string | null {
    return id == null ? null : (this.audioBlobUrls.get(id) ?? null);
  }

  async loadRecordings(silent = false) {
    if (!silent) this.loading = true;
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const params = this.agentExt ? `?officerExtension=${encodeURIComponent(this.agentExt)}` : '';
      const res = await fetch(`${API_URL}/api/v1/telephony/calls/recordings${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      const data = json?.data?.recordings ?? json?.data ?? [];
      this.recordings = Array.isArray(data) ? data : [];
      this.syncTranscriptionPolling();
    } catch {
      this.recordings = [];
      this.syncTranscriptionPolling();
    }
    if (!silent) this.loading = false;
  }

  get filtered(): CallRecording[] {
    const q = this.query.trim().toLowerCase();
    const from = this.fromDate ? new Date(`${this.fromDate}T00:00:00`).getTime() : null;
    const to   = this.toDate   ? new Date(`${this.toDate}T23:59:59`).getTime()   : null;

    return this.recordings
      .filter(r => {
        const ts = r.recordedAt ? new Date(r.recordedAt).getTime() : null;
        if (this.directionFilter !== 'all' && String(r.direction ?? '').toUpperCase() !== this.directionFilter) return false;
        if (from != null && ts != null && ts < from) return false;
        if (to   != null && ts != null && ts > to)   return false;
        if (!q) return true;
        return [r.officerExtension, r.citizenNumber, r.calledNumber, r.direction, r.fileName, r.transcriptText, r.transcriptionStatus, r.wrapUpNotes, r.disposition, r.callReason, r.sourceOfInformation, r.callbackStatus]
          .some(v => String(v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const val = (r: CallRecording): string | number => {
          if (this.sortBy === 'recordedAt')      return r.recordedAt ? new Date(r.recordedAt).getTime() : 0;
          if (this.sortBy === 'durationSeconds') return Number(r.durationSeconds ?? 0);
          return String((r as any)[this.sortBy] ?? '').toLowerCase();
        };
        const av = val(a); const bv = val(b);
        let cmp: number;
        if (av > bv) { cmp = 1; } else if (av < bv) { cmp = -1; } else { cmp = 0; }
        return this.sortDir === 'asc' ? cmp : -cmp;
      });
  }

  sort(key: SortKey) {
    if (this.sortBy === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
    else { this.sortBy = key; this.sortDir = key === 'recordedAt' ? 'desc' : 'asc'; }
  }

  sortIcon(key: SortKey): string {
    if (this.sortBy !== key) return 'unfold_more';
    return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  clearFilters() { this.query = ''; this.directionFilter = 'all'; this.fromDate = ''; this.toDate = ''; }
  formatDuration(s?: number) {
    if (!s) { return '—'; }
    const m = Math.floor(s / 60); const sec = s % 60;
    return m ? `${m}m ${sec}s` : `${sec}s`;
  }
  wrapUpSummary(r: CallRecording) {
    return [
      r.callReason,
      r.sourceOfInformation,
      r.callbackRequired ? `Callback ${r.callbackStatus || 'PENDING'}` : '',
    ].filter(Boolean).join(' | ');
  }
  transcriptionLabel(r: CallRecording): string {
    const status = this.normalizedTranscriptionStatus(r);
    if (r.transcriptText?.trim()) return 'Transcript ready';
    if (status === 'PENDING') return 'Transcription queued';
    if (status === 'PROCESSING') return 'Transcribing recording...';
    if (status === 'FAILED') return 'Transcription failed';
    return 'No transcript saved';
  }
  transcriptionTitle(r: CallRecording): string {
    if (r.transcriptText?.trim()) return 'Click to view full transcript';
    if (r.transcriptionError?.trim()) return r.transcriptionError;
    return this.transcriptionLabel(r);
  }
  transcriptionErrorText(r: CallRecording): string {
    const message = r.transcriptionError?.trim();
    if (!message) return '';
    return message.length > 140 ? `${message.slice(0, 140)}...` : message;
  }
  transcriptionStatusClass(r: CallRecording): string {
    return `ts-${this.normalizedTranscriptionStatus(r).toLowerCase().replace(/_/g, '-')}`;
  }
  isTranscriptionFailed(r: CallRecording): boolean {
    return this.normalizedTranscriptionStatus(r) === 'FAILED' && !r.transcriptText?.trim();
  }
  audioUrl(id?: string | number) { return id ? `${API_URL}/api/v1/telephony/calls/recordings/${id}/audio` : ''; }
  exportUrl() {
    const params = this.agentExt ? `?officerExtension=${encodeURIComponent(this.agentExt)}&limit=1000` : '?limit=1000';
    return `${API_URL}/api/v1/telephony/calls/recordings/export${params}`;
  }
  downloadCsv() { window.open(this.exportUrl(), '_blank'); }
  goToConsole() { void this.router.navigate(['/telephony']); }

  openTranscript(r: CallRecording) {
    if (r.transcriptText?.trim()) this.transcriptModal = r;
  }

  closeTranscript() { this.transcriptModal = null; }

  get parsedTranscript(): TranscriptLine[] {
    const text = this.transcriptModal?.transcriptText;
    if (!text) return [];
    return text.split('\n')
      .map(line => {
        const m = /^\[(\d+:\d+)(?:-\d+:\d+)?\]\s+(.+?):\s+([\s\S]+)$/.exec(line);
        if (!m) return null;
        const [, time, speaker, txt] = m;
        const [min, sec] = time.split(':').map(Number);
        return {
          timeLabel: time,
          startSec: (min * 60) + sec,
          speaker,
          text: txt.trim(),
          isAgent: /\(\d{3,5}\)/.test(speaker),
        };
      })
      .filter((l): l is TranscriptLine => l !== null)
      // Primary: time ascending. Tie: citizen (non-agent) before agent — caller speaks first on inbound calls.
      .sort((a, b) => a.startSec - b.startSec || (a.isAgent ? 1 : -1) - (b.isAgent ? 1 : -1));
  }

  async downloadRecording(recording: CallRecording) {
    const id = recording.callRecordingId;
    if (!id || this.downloadingIds.has(id)) return;

    this.downloadingIds.add(id);
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(this.audioUrl(id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        throw new Error(`Download failed with HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.downloadFileName(recording, res.headers.get('content-disposition'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Call recording download failed', error);
      alert('Recording download failed. Please try again.');
    } finally {
      this.downloadingIds.delete(id);
    }
  }

  private downloadFileName(recording: CallRecording, disposition: string | null): string {
    const headerName = disposition
      ? /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition)?.[1]
      : undefined;
    const fileName = headerName ? decodeURIComponent(headerName.replaceAll('"', '')) : recording.fileName;
    return this.safeFileName(fileName || `call-recording-${recording.callRecordingId}.webm`);
  }

  private safeFileName(fileName: string): string {
    return fileName.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'call-recording.webm';
  }

  private syncTranscriptionPolling() {
    const hasPending = this.recordings.some(r => this.isTranscriptionPending(r));
    if (hasPending && !this.transcriptionPoll) {
      this.transcriptionPoll = setInterval(() => { void this.loadRecordings(true); }, 8000);
    } else if (!hasPending) {
      this.stopTranscriptionPolling();
    }
  }

  private stopTranscriptionPolling() {
    if (!this.transcriptionPoll) return;
    clearInterval(this.transcriptionPoll);
    this.transcriptionPoll = undefined;
  }

  private isTranscriptionPending(r: CallRecording): boolean {
    const status = this.normalizedTranscriptionStatus(r);
    return status === 'PENDING' || status === 'PROCESSING';
  }

  private normalizedTranscriptionStatus(r: CallRecording): string {
    const status = String(r.transcriptionStatus || '').trim().toUpperCase();
    if (status) return status;
    return r.transcriptText?.trim() ? 'COMPLETED' : 'NOT_REQUESTED';
  }
}
